import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { enqueueSnackbar } from '~/utils/notifications';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { domainEventBus, RoiDeletionRequestedEvent } from '~/domain/events';
import {
    OperationPayload,
    StepPayload,
} from '~/engine/util/algorithms/sdf/boolean-operations/types';
import {
    buildPayload as buildPayloadUtil,
    buildStepPayloads,
    StepOperandSelection,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanOperationUtils';
import {
    buildFormulaFromSteps,
    FormulaToken,
    formulaTokens,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanFormula';
import { useGetOrCreateDestination } from './useGetOrCreateDestination';
import {
    computeBooleanPreviewTexture,
    executeBooleanOperations,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanOperationExecution';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { getPreviewColor, PREVIEW_FILL_ALPHA } from './previewColor';
import { generateRandomDicomColor } from '~/utils/color';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import {
    validateROIAndTexture,
    validateStructureNotApproved,
    validateStructureSetWritable,
} from '~/engine/util/algorithms/sdf/boolean-operations/validationUtils';
import { validateStructureName } from '~/utils/validation';
import {
    generateUniqueStructureNameFromList,
    STRUCTURE_NAME_MAX_LENGTH,
} from '~/domain/services/dicomProcessor';
import { usePreviewBase } from './usePreviewBase';
import {
    isOverwriteUnpicked,
    NEW_OUTPUT_TARGET,
    OutputTarget,
    overwrittenRoiNumber,
} from './outputTarget';

/**
 * One operation of the chain: the result so far combined with `operand`.
 * The chain starts from a base structure, and each step runs on the result
 * of the one before it.
 */
export interface BooleanStep {
    id: string;
    operation: BooleanOperation;
    /**
     * The structure combined in, as the structure set holds it now; null
     * until the user picks one, or once it can no longer take part.
     */
    operand: RoiReadModel | null;
}

/** Appended to the base's name to name a new result structure. */
const DEFAULT_NAME_SUFFIX = '_eval';

/** A step as held in state: its operand by ROI number. */
interface StepChoice {
    id: string;
    operation: BooleanOperation;
    operandRoiNumber: number | null;
}

/** The viewport-selected structure when it can take part, else the first. */
function defaultBaseRoiNumber(
    selectable: readonly RoiReadModel[],
    selectedRoi: number
): number | null {
    const selected = selectable.some((r) => r.roiNumber === selectedRoi);
    return selected ? selectedRoi : (selectable[0]?.roiNumber ?? null);
}

export function useBooleanOperation() {
    // The env + structure set + ROI come from the one edit-target resolver,
    // so grid mode operates on the active tile's Environment — operands and
    // the result target can never resolve from another dataset.
    const {
        environment,
        structureSet: currentStructureSet,
        roiNumber: selectedROI,
    } = useActiveEditTarget();
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientRepository = useRecoilValue(patientRepositoryState);

    // Point ROIs (markers, isocenters, registration points) are left out
    // here, in one place.
    const structures = useMemo(
        () => currentStructureSet?.roiArray.filter((r) => !r.isPointRoi) || [],
        [currentStructureSet]
    );

    const isStructureSetApproved = currentStructureSet?.isApproved ?? false;

    // Approved ROIs (or any ROI when the parent structure set is approved)
    // cannot participate in boolean operations as operands or as the result
    // target, so callers default to and pick from the selectable subset.
    const selectableStructures = useMemo(
        () =>
            structures.filter((s) => !(isStructureSetApproved || s.isApproved)),
        [structures, isStructureSetApproved]
    );

    // All ROI names including point ROIs — used for uniqueness validation so
    // new structure names cannot clash with existing markers or isocenters.
    const allRoiNames = useMemo(
        () => currentStructureSet?.roiArray.map((r) => r.name) ?? [],
        [currentStructureSet?.roiArray]
    );

    // Picks are held by ROI number and resolved against the current set: a
    // renamed one shows its new name, an approved or deleted one is dropped.
    const [baseRoiNumber, setBaseRoiNumber] = useState<number | null>(() =>
        defaultBaseRoiNumber(selectableStructures, selectedROI)
    );
    const [stepChoices, setStepChoices] = useState<StepChoice[]>(() => [
        {
            id: uuidv4(),
            operation: BooleanOperation.OR,
            operandRoiNumber: null,
        },
    ]);
    const [editedName, setResultName] = useState<string | null>(null);
    // The colour a new result structure gets, chosen once so the panel can
    // show it before the structure exists.
    const [newStructureColor] = useState(generateRandomDicomColor);
    // Defaults to a new structure: an existing one is written only once the
    // user picks it, keeping the non-destructive path the default.
    const [resultTarget, setResultTarget] =
        useState<OutputTarget>(NEW_OUTPUT_TARGET);
    const resultTargetRoiNumber = overwrittenRoiNumber(resultTarget);

    // The base follows the viewport-selected ROI, skipping approved ones.
    useEffect(() => {
        if (!selectableStructures.length) {
            return;
        }
        setBaseRoiNumber(
            defaultBaseRoiNumber(selectableStructures, selectedROI)
        );
    }, [selectableStructures, selectedROI]);

    const findSelectable = useCallback(
        (roiNumber: number | null) =>
            selectableStructures.find((r) => r.roiNumber === roiNumber) ?? null,
        [selectableStructures]
    );

    const base = findSelectable(baseRoiNumber);

    // A new structure is named after the base until the user names it:
    // `<base>_eval`, unique across every ROI, point ROIs included. A name
    // the user clears stays empty, and Create waits for one.
    const defaultName = useMemo(() => {
        if (!base) {
            return '';
        }
        const stem = base.name.substring(
            0,
            STRUCTURE_NAME_MAX_LENGTH - DEFAULT_NAME_SUFFIX.length
        );
        return generateUniqueStructureNameFromList(
            allRoiNames,
            `${stem}${DEFAULT_NAME_SUFFIX}`
        );
    }, [base, allRoiNames]);
    const resultName = editedName ?? defaultName;
    const steps: BooleanStep[] = useMemo(
        () =>
            stepChoices.map(({ id, operation, operandRoiNumber }) => ({
                id,
                operation,
                operand: findSelectable(operandRoiNumber),
            })),
        [stepChoices, findSelectable]
    );

    // A structure that cannot take part is not kept as a pick.
    const pickable = useCallback(
        (roiNumber: number) => (findSelectable(roiNumber) ? roiNumber : null),
        [findSelectable]
    );

    const selectBase = useCallback(
        (roiNumber: number) => setBaseRoiNumber(pickable(roiNumber)),
        [pickable]
    );

    // A new step subtracts the first unapproved structure the chain does not
    // use yet, or none when every one is in use.
    const addStep = useCallback(() => {
        const used = new Set([
            base?.roiNumber,
            ...steps.map((s) => s.operand?.roiNumber),
        ]);
        const operand = selectableStructures.find(
            (r) => !used.has(r.roiNumber)
        );
        setStepChoices((prev) => [
            ...prev,
            {
                id: uuidv4(),
                operation: BooleanOperation.AND_NOT,
                operandRoiNumber: operand?.roiNumber ?? null,
            },
        ]);
    }, [base, steps, selectableStructures]);

    const removeStep = useCallback((id: string) => {
        setStepChoices((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const setStepOperation = useCallback(
        (id: string, operation: BooleanOperation) => {
            setStepChoices((prev) =>
                prev.map((s) => (s.id === id ? { ...s, operation } : s))
            );
        },
        []
    );

    const setStepOperand = useCallback(
        (id: string, roiNumber: number) => {
            const operandRoiNumber = pickable(roiNumber);
            setStepChoices((prev) =>
                prev.map((s) => (s.id === id ? { ...s, operandRoiNumber } : s))
            );
        },
        [pickable]
    );

    // The chain in the engine's step shape: the base opens the first step,
    // and every later step runs on the previous result.
    const selections: StepOperandSelection[] = useMemo(
        () =>
            steps.map((step, index) => ({
                leftOperand: index === 0 && base ? base : 'previous-result',
                rightOperands: step.operand ? [step.operand] : [],
                operation: step.operation,
            })),
        [base, steps]
    );

    // The formula the selections state, in the order it runs. The payload's
    // formula text comes from formulaTokens too.
    const formula: FormulaToken<RoiReadModel>[] = useMemo(
        () => formulaTokens(selections),
        [selections]
    );

    const patient = useMemo(
        () =>
            patientRepository
                .getAll()
                .find((p) => p.dataSet.PatientID === selectedPatientId),
        [patientRepository, selectedPatientId]
    );

    const findROI = (roiNumber: number): RegionOfInterest | null => {
        return environment?.getRoi(roiNumber) || null;
    };

    // The result can be shown once every step has a structure and an
    // overwrite has a target. The name plays no part in what is shown.
    const canPreview =
        base !== null &&
        steps.length > 0 &&
        steps.every((step) => step.operand !== null) &&
        !isOverwriteUnpicked(resultTarget);

    const isValid = useMemo(() => {
        if (!canPreview) {
            return false;
        }
        // A new structure needs a valid name, the user's or the default one,
        // unique across all ROIs, point ROIs included.
        if (resultTarget.kind === 'new') {
            const [nameValid] = validateStructureName(
                resultName.trim(),
                currentStructureSet?.roiArray.map((r) => r.name) ?? []
            );
            return nameValid;
        }
        return true;
    }, [canPreview, resultTarget, resultName, currentStructureSet?.roiArray]);

    const buildPayload = useCallback((): OperationPayload | null => {
        if (!patient || !currentStructureSet) {
            return null;
        }
        // When writing into an existing ROI the result name is the target
        // ROI's own name — the user doesn't need to supply one.
        let effectiveResultName = resultName.trim();
        if (resultTargetRoiNumber !== null) {
            const targetRoi = currentStructureSet.roiArray.find(
                (r) => r.roiNumber === resultTargetRoiNumber
            );
            if (!targetRoi) {
                enqueueSnackbar('Target structure not found', {
                    variant: 'error',
                });
                return null;
            }
            effectiveResultName = targetRoi.name;
        }
        return buildPayloadUtil(
            selections,
            effectiveResultName,
            isValid,
            patient,
            currentStructureSet
        );
    }, [
        patient,
        currentStructureSet,
        resultName,
        resultTargetRoiNumber,
        selections,
        isValid,
    ]);

    const resolveRightOperands = (
        step: StepPayload
    ): RegionOfInterest[] | null => {
        const rightROIs: RegionOfInterest[] = [];
        for (const rightRef of step.rightOperands) {
            const rightROI = findROI(rightRef.roi.roiNumber);
            const validation = validateROIAndTexture(
                rightROI,
                rightRef.roi.name
            );
            if (!validation.valid) {
                enqueueSnackbar(validation.error, { variant: 'error' });
                return null;
            }
            rightROIs.push(rightROI!);
        }
        return rightROIs;
    };

    /**
     * Converts an OperationPayload (with StructureReferences) to an OperationPayload<RegionOfInterest>
     * (with RegionOfInterest objects). Validates all ROIs before conversion.
     */
    const convertToExecutionPayload = (
        payload: OperationPayload
    ): OperationPayload<RegionOfInterest> | null => {
        if (payload.steps.length === 0) {
            return null;
        }

        const executionSteps: StepPayload<RegionOfInterest>[] = [];

        for (let i = 0; i < payload.steps.length; i++) {
            const step = payload.steps[i]!;

            if (step.leftOperand !== 'previous-result') {
                const approval = validateStructureNotApproved(
                    step.leftOperand.roi
                );
                if (!approval.valid) {
                    enqueueSnackbar(approval.error, { variant: 'error' });
                    return null;
                }
            }

            for (const operand of step.rightOperands) {
                const approval = validateStructureNotApproved(operand.roi);
                if (!approval.valid) {
                    enqueueSnackbar(approval.error, { variant: 'error' });
                    return null;
                }
            }

            if (i === 0) {
                if (step.leftOperand === 'previous-result') {
                    enqueueSnackbar(
                        'Invalid operation: first step cannot use previous result',
                        { variant: 'error' }
                    );
                    return null;
                }

                const sourceROI = findROI(step.leftOperand.roi.roiNumber);
                const sourceValidation = validateROIAndTexture(
                    sourceROI,
                    step.leftOperand.roi.name
                );
                if (!sourceValidation.valid) {
                    enqueueSnackbar(sourceValidation.error, {
                        variant: 'error',
                    });
                    return null;
                }

                const rightROIs = resolveRightOperands(step);
                if (!rightROIs) {
                    return null;
                }
                executionSteps.push({
                    leftOperand: sourceROI!,
                    rightOperands: rightROIs,
                    operation: step.operation,
                });
            } else {
                const rightROIs = resolveRightOperands(step);
                if (!rightROIs) {
                    return null;
                }
                executionSteps.push({
                    leftOperand: 'previous-result',
                    rightOperands: rightROIs,
                    operation: step.operation,
                });
            }
        }

        return {
            steps: executionSteps,
            resultName: payload.resultName,
            formulaText: payload.formulaText,
        };
    };

    const {
        beginOperation: beginPreviewOperation,
        setResult: publishPreview,
        cleanup: clearBooleanPreview,
    } = usePreviewBase({ environment: environment ?? null });

    // Hold the latest `convertToExecutionPayload` in a ref so the
    // useCallback'd `previewOperation` reads the current closure state
    // without having to list a per-render-fresh function in its deps.
    const convertToExecutionPayloadRef = useRef(convertToExecutionPayload);
    convertToExecutionPayloadRef.current = convertToExecutionPayload;

    const previewOperation = useCallback(
        async (payload: OperationPayload): Promise<void> => {
            if (!currentStructureSet || !environment) {
                enqueueSnackbar('Environment not ready', { variant: 'error' });
                return;
            }

            if (!environment.image) {
                enqueueSnackbar('No image loaded — cannot display preview', {
                    variant: 'error',
                });
                return;
            }

            const signal = beginPreviewOperation();

            try {
                const executionPayload =
                    convertToExecutionPayloadRef.current(payload);
                if (!executionPayload) {
                    return;
                }

                // Yield to the browser before heavy GPU computation so the UI
                // can paint (e.g. show a loading state) — matches margin/ring pattern.
                await waitForUI();

                if (signal.aborted) {
                    return;
                }

                const previewTexture =
                    computeBooleanPreviewTexture(executionPayload);
                if (!previewTexture) {
                    enqueueSnackbar('Could not compute preview', {
                        variant: 'warning',
                    });
                    return;
                }

                if (signal.aborted) {
                    previewTexture.delete();
                    return;
                }

                // Use the first source ROI's color for the preview overlay.
                const firstStep = executionPayload.steps[0];
                const sourceColor =
                    firstStep.leftOperand !== 'previous-result'
                        ? firstStep.leftOperand.color
                        : ([1, 1, 1] as [number, number, number]);
                const previewColor = getPreviewColor(sourceColor);

                const previewObj = new CustomTargetObject(
                    environment.image,
                    previewTexture,
                    previewColor
                );
                previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };

                publishPreview(previewObj);
            } catch (error) {
                console.error('Preview operation failed:', error);
                enqueueSnackbar(`Preview failed: ${getErrorMessage(error)}`, {
                    variant: 'error',
                });
            }
        },
        [
            currentStructureSet,
            environment,
            beginPreviewOperation,
            publishPreview,
        ]
    );

    // Previews the chain as it stands, whatever the result will be called.
    const previewChain = useCallback(() => {
        if (!canPreview || !patient || !currentStructureSet) {
            return;
        }
        const previewSteps = buildStepPayloads(
            selections,
            patient,
            currentStructureSet
        );
        if (previewSteps) {
            previewOperation({
                steps: previewSteps,
                resultName: '',
                formulaText: buildFormulaFromSteps(previewSteps),
            });
        }
    }, [
        canPreview,
        patient,
        currentStructureSet,
        selections,
        previewOperation,
    ]);

    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment: environment ?? null,
    });

    const executeOperation = async (
        payload: OperationPayload
    ): Promise<void> => {
        if (!currentStructureSet || !environment) {
            enqueueSnackbar('Environment not ready', { variant: 'error' });
            return;
        }
        // Overwrite chosen with nothing picked: there is nowhere to write.
        if (isOverwriteUnpicked(resultTarget)) {
            return;
        }

        const structureSetValidation =
            validateStructureSetWritable(currentStructureSet);
        if (!structureSetValidation.valid) {
            enqueueSnackbar(structureSetValidation.error, {
                variant: 'error',
            });
            return;
        }

        // Resolve and validate every operand before touching the structure
        // set, so a failed validation never creates a structure that would
        // immediately need deleting again.
        const executionPayload = convertToExecutionPayload(payload);
        if (!executionPayload) {
            return;
        }

        // An existing target is looked up, write-protection included. A null
        // target creates a new structure with the result name, in the colour
        // the panel showed.
        const creatingNew = resultTargetRoiNumber === null;
        const resultROI = await getOrCreateDestination(
            resultTargetRoiNumber,
            payload.resultName,
            { color: newStructureColor }
        );
        if (!resultROI) {
            return;
        }

        // Deletes the structure this operation just created; a pre-existing
        // target is never deleted.
        const rollbackCreated = async () => {
            if (!creatingNew) {
                return;
            }
            try {
                await domainEventBus.publish(
                    new RoiDeletionRequestedEvent(
                        currentStructureSet.dicomUid,
                        resultROI.roiNumber,
                        'user'
                    )
                );
            } catch (deleteError) {
                console.error(
                    'Failed to clean up ROI after error:',
                    deleteError
                );
            }
        };

        try {
            const success = executeBooleanOperations(
                executionPayload,
                resultROI
            );
            if (!success) {
                await rollbackCreated();
                enqueueSnackbar('Boolean operation execution failed', {
                    variant: 'error',
                });
                return;
            }

            if (!resultROI.texture || resultROI.isEmpty) {
                if (creatingNew) {
                    await rollbackCreated();
                    enqueueSnackbar(
                        'Boolean operation produced an empty result (no volume)',
                        { variant: 'error' }
                    );
                } else {
                    // The operation emptied an existing structure (e.g. AND
                    // with no overlap): keep it visible and warn.
                    environment.triggerContourLayerUpdate();
                    enqueueSnackbar(
                        `Boolean operation produced an empty result in "${resultROI.name}"`,
                        { variant: 'warning' }
                    );
                }
                return;
            }

            environment.triggerContourLayerUpdate();
            enqueueSnackbar(
                creatingNew
                    ? `Created "${payload.resultName}" successfully`
                    : `Applied to "${resultROI.name}" successfully`,
                { variant: 'success' }
            );
        } catch (error) {
            console.error('Boolean operation failed:', error);
            await rollbackCreated();
            enqueueSnackbar(
                `Boolean operation failed: ${getErrorMessage(error)}`,
                { variant: 'error' }
            );
        }
    };

    return {
        base,
        selectBase,
        steps,
        addStep,
        removeStep,
        setStepOperation,
        setStepOperand,
        formula,
        resultName,
        setResultName,
        resultTarget,
        setResultTarget,
        structures,
        selectableStructures,
        allRoiNames,
        newStructureColor,
        canPreview,
        isValid,
        buildPayload,
        previewChain,
        clearBooleanPreview,
        executeOperation,
    };
}
